import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AllRouteNames, AllRouteNamesType } from 'src/app/app.routes';
import { AlertTypeDescription } from '@core/domain/model/alerts/alert-type.enum';
import { OmipTerms } from '@pages/market/ui/energy-market/ui/omip/omip-filters/model/omip-filters.model';
import { ProceduresListingTab } from '@core/domain/model/procedures/procedures-listing.model';
import { FAQPageSections } from '@pages/frequently-asked-questions/model/faq.model';

@Injectable({
  providedIn: 'root',
})
export class AcRoutingService {
  constructor(private readonly _router: Router) {}

  static readonly ONELOGIN_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.ONELOGIN];
  goToOneLogin(): void {
    this._router.navigate(AcRoutingService.ONELOGIN_ROUTE());
  }

  static readonly AZURE_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.AZURE];
  goToAzure(): void {
    this._router.navigate(AcRoutingService.AZURE_ROUTE());
  }

  static readonly UNAUTHORIZED_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.UNAUTHORIZED];
  goToUnauthorized(): void {
    this._router.navigate(AcRoutingService.UNAUTHORIZED_ROUTE());
  }

  static readonly HOME_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.HOME];
  goHome(): void {
    this._router.navigate(AcRoutingService.HOME_ROUTE());
  }

  static readonly MY_SERVICES_LISTING_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.SERVICES];
  goToServices(): void {
    this._router.navigate(AcRoutingService.MY_SERVICES_LISTING_ROUTE());
  }

  static readonly MY_SERVICES_SOCIETY_ROUTE = (society: string) => [AllRouteNames.ROOT, AllRouteNames.SERVICES, society];
  goToSociety(society: string): void {
    this._router.navigate(AcRoutingService.MY_SERVICES_SOCIETY_ROUTE(society));
  }

  static readonly MY_SERVICES_INSTALLATION_ROUTE = (society: string, installation: string) => [
    AllRouteNames.ROOT,
    AllRouteNames.SERVICES,
    society,
    installation,
  ];

  static readonly MY_INSTALLATION_CONTRACT_ROUTE = (society: string, installation: string) => [
    ...AcRoutingService.MY_SERVICES_INSTALLATION_ROUTE(society, installation),
    AllRouteNames.CONTRACT,
  ];

  static readonly MY_INSTALLATION_SEPA_ROUTE = (society: string, installation: string) => [
    ...AcRoutingService.MY_SERVICES_INSTALLATION_ROUTE(society, installation),
    AllRouteNames.SEPA,
  ];
  goToInstallation(society: string, installation: string) {
    this._router.navigate(AcRoutingService.MY_SERVICES_INSTALLATION_ROUTE(society, installation));
  }

  static readonly MY_INVOICES_LISTING_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.INVOICES];
  goToInvoices(): void {
    this._router.navigate(AcRoutingService.MY_INVOICES_LISTING_ROUTE());
  }

  static readonly MY_INVOICES_INDIVIDUAL_ROUTE = (invoice: string) => [AllRouteNames.ROOT, AllRouteNames.INVOICES, invoice];
  goToInvoice(invoice: string): void {
    this._router.navigate(AcRoutingService.MY_INVOICES_INDIVIDUAL_ROUTE(invoice));
  }

  static readonly MY_PROCEDURES_LISTING_ROUTE = (tab?: ProceduresListingTab) => {
    const route: AllRouteNamesType[] = [AllRouteNames.ROOT, AllRouteNames.PROCEDURES, AllRouteNames.LISTING];
    if (tab) {
      route.push(tab);
    }
    return route;
  };
  goToProcedures(tab?: ProceduresListingTab) {
    this._router.navigate(AcRoutingService.MY_PROCEDURES_LISTING_ROUTE(tab));
  }

  static readonly PROCEDURE_SELECTOR_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.PROCEDURES, AllRouteNames.PROCEDURE_SELECTOR];
  goToProcedureSelector(params?: { cups?: string }) {
    this._router.navigate(AcRoutingService.PROCEDURE_SELECTOR_ROUTE(), { queryParams: params });
  }
  static readonly BILLING_REPORT_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.INVOICES, AllRouteNames.BILLING_REPORT];
  goToBillingReport() {
    this._router.navigate(AcRoutingService.BILLING_REPORT_ROUTE());
  }
  static readonly USER_MANAGEMENT_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.USER_MANAGEMENT];
  goToUserManagementListing(): void {
    this._router.navigate(AcRoutingService.USER_MANAGEMENT_ROUTE());
  }

  static readonly USER_REGISTER_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.USER_MANAGEMENT, AllRouteNames.USER_REGISTER];
  goToUserRegister(): void {
    this._router.navigate(AcRoutingService.USER_REGISTER_ROUTE());
  }

  static readonly USER_MANAGEMENT_INDIVIDUAL_USER_ROUTE = (id: string, email: string, page: 'edit' | 'data') => [
    AllRouteNames.ROOT,
    AllRouteNames.USER_MANAGEMENT,
    id,
    email,
    page,
  ];
  goToUserManagementUserData(id: string, email: string): void {
    this._router.navigate(AcRoutingService.USER_MANAGEMENT_INDIVIDUAL_USER_ROUTE(id, email, 'data'));
  }
  goToUserManagementEditUser(id: string, email: string): void {
    this._router.navigate(AcRoutingService.USER_MANAGEMENT_INDIVIDUAL_USER_ROUTE(id, email, 'edit'));
  }

  static readonly MY_ACCOUNT_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.MY_ACCOUNT];
  goToMyAccount(): void {
    this._router.navigate(AcRoutingService.MY_ACCOUNT_ROUTE());
  }

  static readonly MY_ACCOUNT_EDIT_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.MY_ACCOUNT, AllRouteNames.EDIT];
  goToMyAccountEdit(): void {
    this._router.navigate(AcRoutingService.MY_ACCOUNT_EDIT_ROUTE());
  }

  static readonly MY_ACCOUNT_CHANGE_PASSWORD_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.MY_ACCOUNT, AllRouteNames.CHANGE_PASSWORD];
  goToMyAccountChangePassword(): void {
    this._router.navigate(AcRoutingService.MY_ACCOUNT_CHANGE_PASSWORD_ROUTE());
  }

  static readonly NOTIFICATIONS_SELECTOR_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.NOTIFICATIONS];
  goToNotificationsSelector() {
    this._router.navigate(AcRoutingService.NOTIFICATIONS_SELECTOR_ROUTE());
  }
  static readonly NOTIFICATIONS_ANNOUNCEMENT_ROUTE = (id: string) => [AllRouteNames.ROOT, AllRouteNames.NOTIFICATIONS, id];
  goToNotificationsAnnouncement(id: string): void {
    this._router.navigate(AcRoutingService.NOTIFICATIONS_ANNOUNCEMENT_ROUTE(id));
  }

  static readonly MY_PROCEDURE_DETAIL_ROUTE = (code: string, path: string) => [AllRouteNames.ROOT, AllRouteNames.PROCEDURES, code, path];
  goToMyProcedureDetailRoute(code: string, path: string) {
    this._router.navigate(AcRoutingService.MY_PROCEDURE_DETAIL_ROUTE(code, path));
  }

  static readonly MY_PROCEDURE_ROUTE = (path: AllRouteNamesType) => [AllRouteNames.ROOT, AllRouteNames.PROCEDURES, path];
  goToMyProcedureRoute(path: AllRouteNamesType, params?: { draftId?: number; cups?: string; invoiceNum?: string }) {
    this._router.navigate(AcRoutingService.MY_PROCEDURE_ROUTE(path), { queryParams: params });
  }

  static readonly ALERTS_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.ALERTS];
  goToAlerts(): void {
    this._router.navigate(AcRoutingService.ALERTS_ROUTE());
  }

  static readonly ALERTS_NEW_ALERT_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.ALERTS, AllRouteNames.NEW_ALERT];
  goToAlertsNewAlert(params?: { alertType?: AlertTypeDescription; period?: OmipTerms }): void {
    this._router.navigate(AcRoutingService.ALERTS_NEW_ALERT_ROUTE(), { queryParams: { params } });
  }

  static readonly ALERTS_EDIT_ALERT_ROUTE = (alertCode: number) => [
    AllRouteNames.ROOT,
    AllRouteNames.ALERTS,
    AllRouteNames.EDIT_ALERT,
    alertCode,
  ];
  goToAlertsEditAlert(alertCode: number): void {
    this._router.navigate(AcRoutingService.ALERTS_EDIT_ALERT_ROUTE(alertCode));
  }

  static readonly COOKIES_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.COOKIES];
  goToCookies(): void {
    this._router.navigate(AcRoutingService.COOKIES_ROUTE());
  }
  static readonly PRIVACY_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.PRIVACY];
  goToPrivacy(): void {
    this._router.navigate(AcRoutingService.PRIVACY_ROUTE());
  }
  static readonly LEGAL_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.LEGAL];
  goToLegal(): void {
    this._router.navigate(AcRoutingService.LEGAL_ROUTE());
  }

  static readonly SUSTAINABILITY_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.SUSTAINABILITY];
  goToSustainability(): void {
    this._router.navigate(AcRoutingService.SUSTAINABILITY_ROUTE());
  }

  static readonly SUSTAINABILITY_CERTIFICATES_ROUTE = () => [
    AllRouteNames.ROOT,
    AllRouteNames.SUSTAINABILITY,
    AllRouteNames.SUSTAINABILITY_CERTIFICATES,
  ];
  goToSustainabilityCertificates(): void {
    this._router.navigate(AcRoutingService.SUSTAINABILITY_CERTIFICATES_ROUTE());
  }

  static readonly MARKET_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.MARKET];
  goToMarket(): void {
    this._router.navigate(AcRoutingService.MARKET_ROUTE());
  }

  static readonly MY_CONSUMPTION_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.MY_CONSUMPTION];
  goToMyConsumption(): void {
    this._router.navigate(AcRoutingService.MY_CONSUMPTION_ROUTE());
  }

  static readonly MY_REPORT_INDIVIDUAL_ROUTE = (idReport: string) => [AllRouteNames.ROOT, AllRouteNames.CONSUMPTION_INVOICES, idReport];

  static readonly MY_CONSUMPTION_MEASUREMENT_DOWNLOAD_ROUTE = () => [
    AllRouteNames.ROOT,
    AllRouteNames.MY_CONSUMPTION,
    AllRouteNames.MEASUREMENT_DOWNLOAD,
  ];
  goToMyConsumptionMeasurementDownload(params?: {
    cups?: string | string[];
    society: string | string[];
    startDate?: string;
    endDate?: string;
  }) {
    this._router.navigate(AcRoutingService.MY_CONSUMPTION_MEASUREMENT_DOWNLOAD_ROUTE(), { queryParams: params });
  }

  static readonly FAQ_ROUTE = () => [AllRouteNames.ROOT, AllRouteNames.FAQ];
  goToFaq(section?: FAQPageSections) {
    this._router.navigate(AcRoutingService.FAQ_ROUTE(), {
      queryParams: { section },
    });
  }
  static readonly FAQ_SPECIFIC_ROUTE = (section: FAQPageSections) => [
    AllRouteNames.ROOT,
    AllRouteNames.FAQ,
    AllRouteNames.MORE_FAQ,
    section,
  ];
  goToFaqSection(section: FAQPageSections) {
    this._router.navigate(AcRoutingService.FAQ_SPECIFIC_ROUTE(section));
  }

  public notFound(): void {
    this._router.navigate([AllRouteNames.ROOT, AllRouteNames.NOT_FOUND]);
  }

  /**
   * Navigate to an absolute URL string. Use this for simple, literal paths.
   */
  goToUrl(url: string): void {
    this._router.navigateByUrl(url);
  }
}
